import { Trans, useTranslation } from "react-i18next";
import { useMemo, useRef, useState } from "react";
import { TextField, Button as AriaButton } from "react-aria-components";
import { Search, X, Plus, Loader2 } from "lucide-react";
import type { Category } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCategories } from "./useCategories.js";
import { useFacilityCategories } from "./useFacilityCategories.js";
import {
  useAttachCategory,
  useDetachCategory,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from "./useCategoryMutations.js";
import { useShowError } from "../../error/ErrorProvider.js";
import { partitionCategories } from "./category-picker.js";
import { CategorySection } from "./CategorySection.js";
import { CategoryRow } from "./CategoryRow.js";
import { EditCategoryDialog } from "./EditCategoryDialog.js";

export interface CategoryPickerProps {
  facilityId: number;
  session: AuthSession | null;
}

/**
 * The category-management body of the Manage-facility drawer. A search box (that also CREATES), then two sections:
 * "On this facility" (attached) and "Available in the library" (unattached), each with an unfiltered count badge.
 * The whole row toggles attach/detach; rename/delete are hover/focus-revealed (see CategoryRow). Per-category counts
 * come from `listCategories` (facilityCount); membership from `getFacilityCategories`. Operation failures → the
 * shared error dialog; only field validation stays inline.
 */
const K = "facilityManagement";

export function CategoryPicker({ facilityId, session }: CategoryPickerProps) {
  const { t } = useTranslation();
  const categories = useCategories(session); // all realm categories (with facilityCount)
  const attached = useFacilityCategories(session, facilityId); // membership for THIS facility
  const attach = useAttachCategory(session);
  const detach = useDetachCategory(session);
  const create = useCreateCategory(session);
  const update = useUpdateCategory(session);
  const del = useDeleteCategory(session);
  const showError = useShowError();

  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null); // restore focus after the clear button clears + unmounts
  // Ids mid-attach/detach/delete — a SET so concurrent row actions gate INDEPENDENTLY (panel).
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set());
  const setBusy = (id: number, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  // Captured when the rename OPENS, alongside the draft (diff panel). Reading the token off the live row at save
  // time would defeat the guard: if the list refetches while the input is open — an attach invalidates it, so does
  // any background refetch — the row's `updatedAt` advances to one that already contains SOMEONE ELSE'S rename, and
  // saving the old draft against that fresh token overwrites them with the CAS satisfied. The token has to describe
  // the state this edit was composed against.
  const [editToken, setEditToken] = useState("");
  // The ICON editor is keyed by id, not by a captured Category, so the open dialog re-renders from FRESH list data
  // after a save — otherwise it would keep showing the icon the row had when it was opened (ADR-043 Phase 2).
  const [iconEditId, setIconEditId] = useState<number | null>(null);

  const attachedIds = useMemo(() => new Set((attached.data ?? []).map((c) => c.id)), [attached.data]);
  const view = useMemo(
    () => partitionCategories(categories.data ?? [], attachedIds, query),
    [categories.data, attachedIds, query],
  );

  const toggle = (c: Category, isAttached: boolean) => {
    setBusy(c.id, true);
    const m = isAttached ? detach : attach;
    m.mutate({ facilityId, categoryId: c.id }, { onError: showError, onSettled: () => setBusy(c.id, false) });
  };
  const remove = (c: Category) => {
    setBusy(c.id, true);
    del.mutate(c.id, { onError: showError, onSettled: () => setBusy(c.id, false) });
  };
  const startRename = (c: Category) => {
    setEditingId(c.id);
    setEditName(c.name);
    setEditToken(c.updatedAt);
  };
  const saveRename = (c: Category) => {
    const name = editName.trim();
    if (!name || name === c.name) return setEditingId(null);
    // Scope the reset to THIS row: if the user has since opened a different row's editor, an in-flight rename
    // resolving must not close (and discard) that other row's edit (panel).
    update.mutate(
      { id: c.id, name, expectedUpdatedAt: editToken },
      { onSuccess: () => setEditingId((prev) => (prev === c.id ? null : prev)), onError: showError },
    );
  };
  // Create the typed name as a new realm category and auto-attach it to THIS facility (the reason you created it here).
  const createFromSearch = () => {
    const name = query.trim();
    if (!name || view.hasExactMatch || create.isPending) return;
    create.mutate(
      { name },
      {
        onSuccess: (cat) => {
          setQuery("");
          searchRef.current?.focus();
          setBusy(cat.id, true);
          attach.mutate({ facilityId, categoryId: cat.id }, { onError: showError, onSettled: () => setBusy(cat.id, false) });
        },
        onError: showError, // 409 CATEGORY_NAME_TAKEN etc. → shared dialog (keep the query so the user can adjust)
      },
    );
  };

  if (categories.isPending || attached.isPending) {
    return <p className="text-muted-foreground py-4 text-sm">{t("facilityManagement.create.loadingCategories")}</p>;
  }
  // Fail closed if EITHER query fails — otherwise membership is empty and every category renders as unattached.
  if (categories.isError || attached.isError) {
    return (
      <p role="alert" className="text-destructive py-4 text-sm">
        {t("facilityManagement.categories.loadFailed")}
      </p>
    );
  }

  const q = query.trim();
  const renderRow = (c: Category, isAttached: boolean) => (
    <CategoryRow
      key={c.id}
      category={c}
      isAttached={isAttached}
      busy={busyIds.has(c.id)}
      editing={editingId === c.id}
      editName={editName}
      savePending={update.isPending}
      onEditNameChange={setEditName}
      onToggle={() => toggle(c, isAttached)}
      onStartRename={() => startRename(c)}
      onSaveRename={() => saveRename(c)}
      onCancelRename={() => setEditingId(null)}
      onDelete={() => remove(c)}
      onEditIcon={() => setIconEditId(c.id)}
    />
  );
  const iconEditing = iconEditId == null ? null : (categories.data ?? []).find((c) => c.id === iconEditId) ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-col gap-2">
        <TextField aria-label={t("facilityManagement.categories.searchLabel")} value={query} onChange={setQuery} className="relative w-full">
          <Search
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2"
          />
          <Input
            ref={searchRef}
            maxLength={255}
            placeholder={t("facilityManagement.categories.searchLabel")}
            className="bg-background border-border pr-8 pl-7"
            onKeyDown={(e) => {
              if (e.key === "Enter" && q !== "" && !view.hasExactMatch) {
                e.preventDefault();
                createFromSearch();
              }
            }}
          />
          {query !== "" ? (
            <AriaButton
              aria-label={t("facilitySelection.clearSearch")}
              onPress={() => {
                setQuery("");
                searchRef.current?.focus();
              }}
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 absolute top-1/2 right-1.5 -translate-y-1/2 cursor-pointer rounded-sm p-0.5 outline-none focus-visible:ring-2"
            >
              <X aria-hidden="true" className="size-4" />
            </AriaButton>
          ) : null}
        </TextField>

        {q !== "" && !view.hasExactMatch ? (
          <div className="border-border flex items-center gap-2 rounded-md border px-3 py-2">
            <Plus aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-sm">
              {/* `Trans`, not string concatenation: the emphasised value sits MID-SENTENCE, and splitting the
                  copy into a prefix and a suffix would hardcode English word order — German puts the verb last
                  ("„Ventile“ erstellen und zuweisen"). The catalogue owns the whole sentence including where the
                  emphasis goes. */}
              <Trans
                i18nKey={`${K}.categories.createAndAttach`}
                components={{ name: <span className="font-medium">{q}</span> }}
              />
            </span>
            <Button size="sm" onPress={createFromSearch} isDisabled={create.isPending}>
              {create.isPending ? <Loader2 aria-hidden="true" className="size-3.5 animate-spin" /> : t("facilityManagement.categories.create")}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <CategorySection
          title={t("facilityManagement.categories.onThisFacility")}
          total={view.attachedTotal}
          isEmpty={view.attached.length === 0}
          emptyMessage={q !== "" ? t("facilityManagement.categories.noneAttachedMatch") : t("facilityManagement.categories.noneAttached")}
        >
          {view.attached.map((c) => renderRow(c, true))}
        </CategorySection>

        <CategorySection
          title={t("facilityManagement.categories.inLibrary")}
          total={view.libraryTotal}
          isEmpty={view.library.length === 0}
          emptyMessage={q !== "" ? t("facilityManagement.categories.nothingElseMatches", { query: q }) : t("facilityManagement.categories.allAttached")}
        >
          {view.library.map((c) => renderRow(c, false))}
        </CategorySection>
      </div>

      {iconEditing && (
        <EditCategoryDialog
          category={iconEditing}
          session={session}
          onClose={() => setIconEditId(null)}
          onSave={async (imageId) => {
            try {
              // `imageId` only — a rename must not ride along, and omitting name/description leaves them untouched.
              // `iconEditing` is re-derived from the LIVE list each render, so its token is the freshest one the
              // client has — which is what makes a second edit after a save succeed rather than 409 against itself.
              await update.mutateAsync({ id: iconEditing.id, imageId, expectedUpdatedAt: iconEditing.updatedAt });
            } catch (e) {
              showError(e);
            }
          }}
        />
      )}
    </div>
  );
}
